import type { DragEndEvent } from "@dnd-kit/core";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useId } from "react";
import { cn } from "../../lib/utils";
import { Button } from "./Button";
import { Input } from "./Input";
import { Select } from "./Select";

export type ObjectArraySchema = {
  type?: string;
  items?: {
    type?: string;
    properties?: Record<
      string,
      { type?: string; description?: string; enum?: string[]; default?: unknown }
    >;
    required?: string[];
  };
};

type ObjectArrayEditorProps = {
  value: Record<string, unknown>[];
  onChange: (value: Record<string, unknown>[]) => void;
  schema: ObjectArraySchema;
};

type FieldDef = {
  key: string;
  required: boolean;
  description?: string;
  enumValues?: string[];
  defaultValue?: unknown;
  /** If true, this field is auto-derived from another field and hidden from the UI. */
  derived?: boolean;
};

function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/** Convert a label like "Lobby Code" to camelCase key "lobbyCode". */
function toCamelCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, c: string) => c.toUpperCase())
    .replace(/^[A-Z]/, (c) => c.toLowerCase())
    .replace(/[^a-zA-Z0-9]/g, "");
}

function getFieldDefs(schema: ObjectArraySchema): FieldDef[] {
  const props = schema.items?.properties ?? {};
  const required = new Set(schema.items?.required ?? []);
  return Object.entries(props)
    .filter(([key]) => key !== "id")
    .map(([key, prop]) => ({
      key,
      required: required.has(key),
      description: prop.description,
      enumValues: prop.enum,
      defaultValue: prop.default,
      // Hide 'key' field when 'label' exists — key is auto-derived from label
      derived: key === "key" && "label" in props,
    }));
}

function formatFieldLabel(str: string): string {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/_/g, " ");
}

function formatEnumLabel(str: string): string {
  return str.replace(/[_-]/g, " ").replace(/^./, (s) => s.toUpperCase());
}

export function ObjectArrayEditor({ value, onChange, schema }: ObjectArrayEditorProps) {
  const idPrefix = useId();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const fields = getFieldDefs(schema);

  const items = value.map((v, i) => ({
    id: `${idPrefix}-${i}`,
    data: v,
  }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    const next = [...value];
    const [removed] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, removed);
    onChange(next);
  };

  const handleAdd = () => {
    const newItem: Record<string, unknown> = { id: "" };
    for (const f of fields) {
      newItem[f.key] = f.defaultValue ?? f.enumValues?.[0] ?? "";
    }
    onChange([...value, newItem]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, fieldKey: string, fieldValue: string) => {
    const next = [...value];
    const item = { ...next[index], [fieldKey]: fieldValue };
    // Auto-derive 'key' from 'label' when label changes
    if (fieldKey === "label") {
      const keyField = fields.find((f) => f.key === "key" && f.derived);
      if (keyField) {
        item.key = toCamelCase(fieldValue) || (item.key as string) || `field_${index + 1}`;
      }
    }
    if (fieldKey === "name") {
      const generatedId = slugify(fieldValue);
      item.id = generatedId || (item.id as string) || `item_${index + 1}`;
    }
    next[index] = item;
    onChange(next);
  };

  if (value.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-xs text-surface-400 mb-2">No items</p>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Item
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => (
            <SortableObjectItem
              key={item.id}
              id={item.id}
              data={item.data}
              fields={fields}
              onChange={(key, val) => handleFieldChange(index, key, val)}
              onRemove={() => handleRemove(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button variant="ghost" size="sm" onClick={handleAdd} className="w-full">
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add Item
      </Button>
    </div>
  );
}

type SortableObjectItemProps = {
  id: string;
  data: Record<string, unknown>;
  fields: FieldDef[];
  onChange: (key: string, value: string) => void;
  onRemove: () => void;
};

function SortableObjectItem({ id, data, fields, onChange, onRemove }: SortableObjectItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const visibleFields = fields.filter((f) => !f.derived);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "rounded-lg border border-surface-700 bg-surface-800/50 p-2.5 group",
        isDragging && "opacity-50",
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          className="p-1 cursor-grab active:cursor-grabbing text-surface-500 hover:text-surface-300"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-surface-500 hover:text-error-400 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        {visibleFields.map((field) =>
          field.enumValues ? (
            <div key={field.key}>
              <label className="block text-xs font-medium text-surface-400 mb-0.5">
                {formatFieldLabel(field.key)}
              </label>
              <Select
                size="xs"
                options={field.enumValues.map((v) => ({
                  value: v,
                  label: formatEnumLabel(v),
                }))}
                value={(data[field.key] as string) ?? field.enumValues[0] ?? ""}
                onChange={(v) => onChange(field.key, v)}
                placeholder={formatFieldLabel(field.key)}
              />
            </div>
          ) : (
            <Input
              key={field.key}
              size="xs"
              label={formatFieldLabel(field.key)}
              value={(data[field.key] as string) ?? ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              placeholder={field.required ? "Required" : "Optional"}
            />
          ),
        )}
      </div>
    </div>
  );
}
